import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';

/**
 * Rende una pagina dinamica basata su blocchi.
 * Tipi supportati: text, image, video, iframe, banner, gallery
 */
export default function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getPageBySlug(slug)
      .then((p) => { if (alive) { setPage(p); setError(null); } })
      .catch((e) => { if (alive) setError(e?.message || 'Pagina non trovata'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="text-pink-brand animate-spin" size={32} />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="glass-card p-8 max-w-md text-center" data-testid="dynamic-page-error">
          <AlertTriangle className="text-pink-brand mx-auto mb-3" size={32} />
          <h1 className="text-white font-bold text-xl mb-2">Pagina non trovata</h1>
          <p className="text-white/60 text-sm mb-4">{error || 'La pagina cercata non esiste.'}</p>
          <Link to="/" className="btn-primary inline-flex !py-2 !px-5">Torna alla home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-navy text-white" data-testid={`dynamic-page-${page.slug}`}>
      <header className="py-12 lg:py-20 px-6 text-center bg-gradient-to-b from-navy-dark to-navy">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase">{page.title}</h1>
        {page.subtitle && <p className="text-pink-brand mt-3 text-base lg:text-lg font-semibold">{page.subtitle}</p>}
      </header>
      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-10 lg:py-14 space-y-8">
        {(page.blocks || []).map((b, i) => <Block key={b.id || i} block={b} />)}
        {(!page.blocks || page.blocks.length === 0) && (
          <div className="text-white/50 text-center py-10">Questa pagina non ha ancora contenuti.</div>
        )}
      </div>
    </div>
  );
}

function Block({ block }) {
  const t = block.type;
  if (t === 'text') {
    return (
      <div
        className="prose prose-invert max-w-none text-white/85 leading-relaxed"
        data-testid="block-text"
        dangerouslySetInnerHTML={{ __html: simpleMarkdown(block.content || '') }}
      />
    );
  }
  if (t === 'image') {
    if (!block.src) return null;
    return (
      <figure className="my-2" data-testid="block-image">
        <img src={block.src} alt={block.alt || ''} className="w-full rounded-2xl" />
        {block.caption && <figcaption className="text-center text-white/55 text-sm mt-2 italic">{block.caption}</figcaption>}
      </figure>
    );
  }
  if (t === 'video') {
    if (!block.src) return null;
    const isYoutube = /youtu\.?be/.test(block.src);
    if (isYoutube) {
      const id = ytId(block.src);
      return (
        <div className="aspect-video rounded-2xl overflow-hidden bg-black" data-testid="block-video">
          <iframe src={`https://www.youtube.com/embed/${id}`} title={block.caption || 'video'} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen></iframe>
        </div>
      );
    }
    return (
      <video src={block.src} controls className="w-full rounded-2xl bg-black" data-testid="block-video">
        Il tuo browser non supporta il tag video.
      </video>
    );
  }
  if (t === 'iframe') {
    if (!block.src) return null;
    return (
      <div className="rounded-2xl overflow-hidden bg-black" style={{ height: block.height || 480 }} data-testid="block-iframe">
        <iframe src={block.src} title={block.caption || 'embed'} className="w-full h-full" allow="fullscreen"></iframe>
      </div>
    );
  }
  if (t === 'banner') {
    return (
      <div
        className="relative rounded-2xl overflow-hidden p-8 lg:p-14 text-center"
        style={{ backgroundImage: block.bg ? `url(${block.bg})` : 'linear-gradient(135deg, var(--theme-primary), var(--theme-accent))', backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="block-banner"
      >
        <div className="absolute inset-0 bg-navy-dark/55"></div>
        <div className="relative z-10">
          {block.title && <h2 className="font-display text-3xl lg:text-5xl font-bold uppercase">{block.title}</h2>}
          {block.subtitle && <p className="text-white/85 mt-3 text-base lg:text-lg">{block.subtitle}</p>}
          {block.cta_label && block.cta_url && (
            <a href={block.cta_url} target={block.cta_url.startsWith('http') ? '_blank' : '_self'} rel="noreferrer" className="btn-primary inline-flex mt-5">{block.cta_label}</a>
          )}
        </div>
      </div>
    );
  }
  if (t === 'gallery') {
    const items = block.items || [];
    if (items.length === 0) return null;
    return (
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="block-gallery">
        {items.map((it, idx) => (
          <a key={idx} href={it.src} target="_blank" rel="noreferrer" className="block group">
            <img src={it.src} alt={it.alt || ''} className="w-full aspect-square object-cover rounded-xl group-hover:scale-[1.02] transition-transform" />
          </a>
        ))}
      </div>
    );
  }
  return null;
}

function simpleMarkdown(s) {
  // mini parser: **bold**, *italic*, [text](url), \n\n -> p, \n -> br
  const escapeHtml = (str) => str
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const parts = escapeHtml(s).split(/\n{2,}/).map((p) => {
    let out = p
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*(?!\*)([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-pink-brand underline">$1</a>')
      .replace(/\n/g, '<br/>');
    return `<p>${out}</p>`;
  });
  return parts.join('');
}

function ytId(url) {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{6,})/);
  return m ? m[1] : '';
}
